import React, {
  useMemo, useContext, useCallback, useState, useRef, ForwardedRef, ComponentType, ReactElement,
} from 'react';
import { useDispatch } from 'react-redux';
import axios, { Method, AxiosProgressEvent } from 'axios';
import noop from 'lodash/noop';

import { UploadStatus } from 'ia-react-core/~/interfaces/UploadStatus';
import useScopedSelector from 'ia-react-core/~/hooks/useScopedSelector';
import updateFormById from '../../../actions/form-fn/updateFormById';
import set from '../../../utils/accessors/set';
import get from '../../../utils/accessors/get';
import excludePropsByComponent from '../../../utils/components/_excludePropsByComponent';
import FormContext from '../FormContext';
import Control, { ControlProps, ControlRenderProps } from '../Control';
import { UploadProps } from '../_Upload';

import { StyledFileList } from './UploadControl.styles';

export type UploadControlValidationProps = {
  /** Adds the `required` validator to the control. The control will be invalid if the list of files is empty. */
  required?: boolean;
  /** Adds the `minFiles` validator to the control. The control will be invalid is there are less files than `minFiles`. */
  minFiles?: number;
  /** Adds the `maxFiles` validator to the control. The control will be invalid is there are more files than `maxFiles`. */
  maxFiles?: number;
};

export interface UploadControlProps<Value = (File | string)[], StateValue = File[]> extends
Omit<UploadProps, 'defaultValue' | 'name' | 'value' | 'children' | 'onTouched' | 'onValueChange'>,
Omit<ControlProps<Value, StateValue>, 'children'>,
UploadControlValidationProps {
  /** URL of the upload service. If provided, the files will be sent to the API as soon as there are selected by the uses; otherwise, they will be sent as part of the form itself. */
  addUrl?: string;
  /** HTTP method of the service to add an upload. */
  addMethod?: Method;
  /** URL of the service to remove a file. */
  removeUrl?: string;
  /** HTTP method of the service to remove an upload. */
  removeMethod?: Method;
  /** Path to the id of a file on the data returned by the upload service. */
  idPath?: string;
  /** Append the file id to the removeUrl using . */
  appendRemoveId?: string;
  target?: HTMLInputElement;
  /** Allows multiple files to be selected. */
  multiple?: boolean;
  /** Name of the multipart field when using addUrl */
  multipartFieldName?: string;
  /** Element to use as the dropdown target */
  Target: ComponentType<{
    name?: string;
    value?: Value;
    onValueChange: (newFiles: File[]) => void;
    onTouched: () => void;
    isValid: boolean;
    isTouched: boolean;
    multiple: boolean;
  }>;
  defaultValue?: ControlProps<Value, StateValue>['defaultValue'];
}

interface UploadControlInternalProps<Value> extends Omit<ControlRenderProps<Value, HTMLInputElement>, 'onValueChangeCallback' | 'id'> {
  ref?: ForwardedRef<HTMLInputElement>;
  onValueChangeCallback: ControlRenderProps<Value, HTMLInputElement>['onValueChange'];
  onTouchedCallback: ControlRenderProps<Value, HTMLInputElement>['onTouched'];
}

export interface UploadControlComposedProps<Value, StateValue> extends UploadControlProps<Value, StateValue>, UploadControlInternalProps<Value> { }

export const UploadControlDefaultProps: Partial<UploadControlProps<any, any>> = {
  defaultValue: [],
  addMethod: 'POST',
  removeMethod: 'DELETE',
  idPath: 'id',
  appendRemoveId: 'param',
  multiple: false,
  multipartFieldName: 'file',
};

const UploadControl = <Value extends unknown[] = (File | string)[], StateValue = File[]>({
  addUrl, addMethod, removeUrl, removeMethod, idPath, appendRemoveId, Target, name, required, minFiles, maxFiles, multiple, isTouched, isValid, value, multipartFieldName, onValueChange, onTouched, onValueChangeCallback, onTouchedCallback, ...props
}: UploadControlComposedProps<Value, StateValue>): ReactElement<UploadControlComposedProps<Value, StateValue>> => {
  const dispatch = useDispatch();
  const { parentFormId, valuesDst, setControlValue } = useContext(FormContext);
  const defaultFiles = useScopedSelector<Record<string, File>>(`${valuesDst}.${name}__files`);
  const defaultStatuses = useMemo<UploadStatus[]>(() => {
    if (!value?.length) return [];
    if (typeof value[0] === 'object') {
      return (value as File[]).map((file) => ({ file, success: true, error: null, progress: 100, source: null }));
    }
    if (defaultFiles) {
      return (value as string[]).map((id) => ({ file: defaultFiles[id], success: true, error: null, progress: 100, source: null }));
    }
    return [];
  }, [defaultFiles, value]);
  const [statuses, setStatuses] = useState<UploadStatus[]>(defaultStatuses);
  const hasFiles = Boolean(statuses?.length);
  const statusesRef = useRef(statuses);
  const defaultRef = useMemo<Value>(() => {
    if (defaultFiles) {
      return addUrl ? Object.keys(defaultFiles) as Value : Object.values(defaultFiles) as Value;
    }
    return [] as Value;
  }, [addUrl, defaultFiles]);
  const filesRef = useRef<Value>(defaultRef);

  const updateFormFiles = useCallback((ids: string[]) => {
    setControlValue(`${name}__files`, ids.reduce<Record<string, File>>((result, currentId, index) => {
      result[currentId] = statusesRef.current[index].file;
      return result;
    }, {}));
  }, [name, setControlValue]);

  const updateFileStatus = useCallback((file: File, newStatus: UploadStatus) => (
    new Promise<void>((resolve) => {
      setStatuses((latestStatuses) => {
        const fileIndex = latestStatuses.findIndex((status) => file === status.file);
        const newStatuses = [...latestStatuses];
        newStatuses[fileIndex] = {
          ...newStatuses[fileIndex],
          ...newStatus,
        };
        statusesRef.current = newStatuses;

        resolve();

        return newStatuses;
      });
    })
  ), []);

  const getFormData = useCallback((file: File) => {
    const formData = new FormData();
    formData.append(multipartFieldName, file);
    return formData;
  }, [multipartFieldName]);

  const uploadProgress = useCallback(async (event: AxiosProgressEvent, file: File) => {
    await updateFileStatus(file, {
      progress: Math.floor(100 / (event.total / event.loaded)) || 0,
    });
  }, [updateFileStatus]);

  const sendUploadRequest = useCallback(async (file: File) => {
    const formData = getFormData(file);
    const cancelTokenSource = axios.CancelToken.source();

    await updateFileStatus(file, {
      source: cancelTokenSource,
      error: null,
    });

    return axios.request({
      url: addUrl,
      method: addMethod,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (event: AxiosProgressEvent) => {
        uploadProgress(event, file).catch(noop);
      },
      cancelToken: cancelTokenSource.token,
    });
  }, [addMethod, addUrl, getFormData, updateFileStatus, uploadProgress]);

  const handleFileUploadSuccess = useCallback(async (data: unknown, file: File) => {
    await updateFileStatus(file, {
      success: data,
      error: null,
    });

    const latestStatuses = statusesRef.current;
    const fileIndex = latestStatuses.findIndex((status) => file === status.file);

    const updatedFiles = [...filesRef.current] as Value;
    const id: string = get(latestStatuses[fileIndex].success, idPath);

    updatedFiles.splice(fileIndex, 0, id);
    filesRef.current = updatedFiles;
    onValueChangeCallback(updatedFiles);
    updateFormFiles(updatedFiles as string[]);
    if (onValueChange) onValueChange(updatedFiles);
  }, [idPath, onValueChange, onValueChangeCallback, updateFormFiles, updateFileStatus]);

  const handleFileUploadError = useCallback(async (error: Error, file: File) => {
    await updateFileStatus(file, {
      success: null,
      error,
    });
  }, [updateFileStatus]);

  const uploadFile = useCallback((file: File) => {
    // eslint-disable-next-line @typescript-eslint/unbound-method
    const currentThen = Promise.prototype.then;

    // AppDynamics monkey-patches Promise#then wich causes infinite loops with axios. We temporarily deativate.
    // eslint-disable-next-line @typescript-eslint/no-floating-promises, no-extend-native
    Object.defineProperty(Promise.prototype, 'then', {
      configurable: true,
      writable: true,
      // eslint-disable-next-line @typescript-eslint/unbound-method
      value: window.ADRUM?.utils?.refs?.promiseThen || Promise.prototype.then,
    });

    sendUploadRequest(file)
      .then(({ data }: { data: unknown }) => handleFileUploadSuccess(data, file))
      .catch((error: Error) => handleFileUploadError(error, file))
      .finally(() => {
        const isLastUpload = !statusesRef.current.some((status) => status.progress);
        /* istanbul ignore next */
        if (isLastUpload) {
          // eslint-disable-next-line no-extend-native, @typescript-eslint/no-floating-promises
          Object.defineProperty(Promise.prototype, 'then', {
            configurable: true,
            writable: true,
            value: currentThen,
          });
        }
      });
  }, [handleFileUploadError, handleFileUploadSuccess, sendUploadRequest]);

  const handleRetry = useCallback((index: number) => {
    const { file } = statuses[index];
    uploadFile(file);
  }, [statuses, uploadFile]);

  const addUploadFiles = useCallback((newFiles: File[]) => {
    newFiles.forEach((file) => {
      uploadFile(file);
    });
  }, [uploadFile]);

  const handleValueChange = useCallback((newFiles: File[]) => {
    setStatuses((latestStatuses) => {
      const newStatuses = newFiles.map((file) => ({
        file,
        progress: addUrl ? 0 : 100,
        source: null,
        success: addUrl ? null : {},
        error: null,
      }));
      const updatedStatuses = (multiple) ? [
        ...newStatuses,
        ...latestStatuses,
      ] : newStatuses;
      statusesRef.current = updatedStatuses;
      return updatedStatuses;
    });

    if (addUrl) {
      addUploadFiles(newFiles);
    } else {
      const updatedFiles = (multiple) ? [...newFiles, ...filesRef.current] as Value : newFiles as Value;
      filesRef.current = updatedFiles;
      onValueChangeCallback(updatedFiles);
      if (onValueChange) onValueChange(updatedFiles);
    }
  }, [addUploadFiles, addUrl, multiple, onValueChange, onValueChangeCallback]);

  const handleTouched = useCallback(() => {
    onTouchedCallback(filesRef.current);
    if (onTouched) onTouched(filesRef.current);
  }, [onTouched, onTouchedCallback]);

  const removeUploadFile = useCallback((index: number) => {
    if (statuses[index].success) {
      const id = get(statuses[index].success, idPath);
      axios
        .request({
          url: appendRemoveId === 'param' ? `${removeUrl}/${id}` : removeUrl,
          method: removeMethod,
        })
        // Ignore deletion errors as not critical
        .catch(noop);
    }
  }, [appendRemoveId, idPath, removeMethod, removeUrl, statuses]);

  const handleDelete = useCallback((index: number) => {
    const status = statusesRef.current[index];
    if (status) {
      if (status.success) {
        const updatedFiles = [...filesRef.current] as Value;
        updatedFiles.splice(index, 1);
        const formState = {};
        const values = set({}, name, updatedFiles);
        const options = { mergeBlacklist: [updatedFiles] };
        dispatch(updateFormById(parentFormId, formState, values, options));
        filesRef.current = updatedFiles;
        onValueChangeCallback(updatedFiles);
        updateFormFiles(updatedFiles as string[]);
        if (onValueChange) onValueChange(updatedFiles);
      }

      setStatuses((latestStatuses) => {
        if (!latestStatuses[index].success) {
          latestStatuses[index]?.source?.cancel('Upload cancelled');
        }
        const newStatuses = [...latestStatuses];
        newStatuses.splice(index, 1);
        statusesRef.current = newStatuses;
        return newStatuses;
      });

      if (removeUrl) removeUploadFile(index);
    }
  }, [removeUrl, removeUploadFile, name, dispatch, parentFormId, onValueChangeCallback, updateFormFiles, onValueChange]);

  const fileList = useMemo(() => statuses.map(({ file }) => file), [statuses]);

  return (
    <>

      <Target
        data-testid="UploadControl"
        aria-invalid={Boolean(!isValid && isTouched)}
        data-control-nesting="1"
        {...excludePropsByComponent(props, Control)}
        name={name}
        value={value}
        onValueChange={handleValueChange}
        onTouched={handleTouched}
        isValid={isValid}
        isTouched={isTouched}
        multiple={multiple}
      />
      {hasFiles && (
        <StyledFileList
          files={fileList}
          statuses={statuses}
          onRetry={handleRetry}
          onDelete={handleDelete}
        />
      )}
    </>
  );
};

UploadControl.defaultProps = UploadControlDefaultProps;

export default UploadControl;
